<template>
  <main id="content" aria-labelledby="main-title">

    <ElementHeroInfo :content="{
      title: 'How reporting with Elker works',
      subtitle: 'However your organisation has asked you to speak up, this is how our platform keeps your report secure and your information private.',
      pretitle: 'For reporters'
    }" />

    <!-- Inline support line under the hero: a quiet sentence with two text links
    that jump to the full support box at the bottom (#get-support). Boxless, but
    framed by hairline rules top and bottom so it reads as an intentional band and
    breaks the page between the hero and the first explainer section, rather than
    floating loose. Language is scoped to technical/platform help, not general
    support, so we don't invite report-content queries Elker can't act on. -->
    <div class="py-5 md:py-6">
      <Container>
        <FadeIn class="flex justify-center">
          <div class="inline-block max-w-[880px] border-2 border-[#d7d4cd] rounded-[16px] bg-white px-6 md:px-8 py-4 md:py-5 text-center">
            <p class="text-[.95rem] leading-[1.6rem] text-gray-700">
              Need technical support using Elker?
              <a href="#get-support" @click="openSupport('message')" class="font-semibold text-[#3645d0] underline decoration-[#c0c7fa] underline-offset-2 hover:decoration-[#3645d0]">Send us a message</a>
              or
              <a href="#get-support-anonymous" @click="openSupport('anonymous')" class="font-semibold text-[#3645d0] underline decoration-[#c0c7fa] underline-offset-2 hover:decoration-[#3645d0]">contact us anonymously</a>.
            </p>
          </div>
        </FadeIn>
      </Container>
    </div>

    <Section background="#e8e5de">
      <Container>

        <header class="flex flex-col md:text-center md:items-center mx-auto max-w-2xl justify-center mb-fluid-sm">
          <FadeIn>
            <div class="mb-2 md:mb-4">
              <span class="rounded-full bg-[#5a6ded] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
                Platform protections
              </span>
            </div>
            <h2 class="text-section-title">How the platform protects you</h2>
            <div class="text-subtitle">
              Elker is built so that only the reviewers your organisation has nominated can read a report. Your manager, your employer's IT team, and Elker's own staff are kept out of the platform by design. These controls apply to every report, on every deployment.
            </div>
          </FadeIn>
        </header>

        <div class="mt-8 md:mt-16 mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 xl:gap-8 max-w-[1200px]">
          <FadeIn
          v-for="(item, idx) in protections"
          :key="item.title"
          :delay="(idx % 3) * 80"
          >
            <div class="h-full bg-white border-2 border-[#d7d4cd] rounded-[20px] p-6 md:p-8">
              <div class="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-[#5a6ded] mb-5">
                <Icon :name="item.icon" class="h-6 w-6 text-white" />
              </div>
              <h3 class="text-lg md:text-xl font-bold tracking-tight text-gray-900 mb-3 leading-tight">
                {{ item.title }}
              </h3>
              <p class="text-[.95rem] md:text-[1rem] leading-[1.65rem] text-gray-700" v-html="item.body"></p>
            </div>
          </FadeIn>
        </div>

      </Container>
    </Section>

    <Section>
      <Container>

        <header class="flex flex-col md:text-center md:items-center mx-auto max-w-2xl justify-center">
          <FadeIn>
            <div class="mb-2 md:mb-4">
              <span class="rounded-full bg-[#5a6ded] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
                Your reporting journey
              </span>
            </div>
            <h2 class="text-section-title">What happens, step by step</h2>
            <div class="text-subtitle">
              The questions you're asked will differ by organisation. The actions you take, and the way your information is handled, are the same everywhere.
            </div>
          </FadeIn>
        </header>

        <FadeIn class="mt-8 md:mt-12 mx-auto max-w-[720px]">
          <ol class="divide-y divide-[#c0c7fa]/60" role="list">
            <li
            v-for="(step, index) in journeySteps"
            :key="'step-'+index"
            class="grid grid-cols-[48px,1fr] md:grid-cols-[88px,1fr] gap-4 md:gap-8 items-start py-4 md:py-6 first:pt-0 last:pb-0"
            >
              <div class="lora text-[2rem] md:text-[3.25rem] font-medium leading-none text-[#5a6ded] tabular-nums tracking-tight">
                {{ String(index + 1).padStart(2, '0') }}
              </div>
              <div class="min-w-0">
                <h3 class="text-lg md:text-xl font-bold tracking-tight text-gray-900 leading-tight mb-2">
                  {{ step.title }}
                </h3>
                <p class="text-sm md:text-base text-gray-700 leading-relaxed">
                  {{ step.body }}
                </p>
              </div>
            </li>
          </ol>
        </FadeIn>

        <FadeIn class="mt-12 md:mt-16 mx-auto max-w-[1080px]">
          <div class="rounded-[20px] bg-[#eef0ff] border-2 border-[#c0c7fa] px-5 md:px-8 py-5 md:py-6 flex flex-col md:flex-row items-start gap-4">
            <div class="flex-none flex items-center justify-center h-10 w-10 rounded-full bg-white border-2 border-[#c0c7fa]">
              <Icon name="material-symbols:lightbulb-outline" class="h-5 w-5 text-[#3645d0]" />
            </div>
            <div class="flex-1">
              <span class="font-semibold text-gray-900">A tip for extra privacy:</span>
              <span class="text-[.95rem] leading-[1.65rem] text-gray-700">
                make a note of your organisation's Elker link and consider submitting your report from a personal device, outside your work network. The platform works from anywhere with an internet connection.
              </span>
            </div>
          </div>
        </FadeIn>

      </Container>
    </Section>

    <Section>
      <Container>

        <FadeIn class="rounded-[20px] md:rounded-[35px] bg-[#e2e4e9] border-2 border-[#d0d4df] px-6 md:px-12 py-10 md:py-16">

          <div class="mx-auto max-w-2xl lg:text-center">
            <div class="mb-2 md:mb-4">
              <span class="rounded-full bg-[#5a6ded] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
                Our commitments
              </span>
            </div>
            <h2 class="text-section-title">What we don't do</h2>
            <div class="text-subtitle">
              Some concrete promises about what your information is never used for.
            </div>
          </div>

          <ul role="list" class="mt-10 md:mt-12 mx-auto max-w-[1080px] grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <li
            v-for="item in commitments"
            :key="item"
            class="flex gap-4 items-start bg-white rounded-xl p-5 border-2 border-[#d0d4df]"
            >
              <div class="flex-none flex items-center justify-center h-8 w-8 rounded-full bg-[#5a6ded]">
                <Icon name="material-symbols:check" class="h-5 w-5 text-white" />
              </div>
              <span class="text-[.95rem] md:text-[1rem] leading-[1.6rem] text-gray-800">{{ item }}</span>
            </li>
          </ul>

        </FadeIn>

      </Container>
    </Section>

    <Section background="#e8e5de">
      <Container>

        <FadeIn class="mx-auto max-w-[880px]">

          <header class="flex flex-col md:text-center md:items-center mb-8 md:mb-10">
            <div class="mb-2 md:mb-4">
              <span class="rounded-full bg-[#5a6ded] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
                Common questions
              </span>
            </div>
            <h2 class="text-section-title">Answers to what reporters ask</h2>
          </header>

          <div class="relative">
            <div
            v-for="(faq, index) in faqs"
            :key="'faq-'+index"
            class="border-2 border-[#d7d4cd] hover:border-[#5a6ded] transition bg-white rounded-xl mb-4 duration-200"
            >
              <button
              @click="togglePanel(index)"
              class="pointer py-4 md:py-6 w-full text-left"
              :aria-expanded="activePanel === index"
              :aria-controls="`how-faq-content-${index}`"
              :aria-label="`Toggle FAQ: ${faq.q}`"
              >
                <div class="flex w-full items-start justify-between text-left items-center">
                  <div class="text-base font-semibold leading-6 w-[calc(100%-4rem)] lg:text-lg px-4">
                    {{ faq.q }}
                  </div>
                  <div class="mr-4 bg-[#5a6ded] border-2 border-[#3645d0] rounded w-8 h-8 flex items-center justify-center flex-none">
                    <Icon name="ph:caret-down" v-if="activePanel !== index" class="h-6 w-6 text-[#fff]" />
                    <Icon name="ph:caret-up" v-else class="h-6 w-6 text-[#fff]" />
                  </div>
                </div>
              </button>
              <div
              v-show="activePanel === index"
              class="prose max-w-none"
              :id="`how-faq-content-${index}`"
              role="region"
              >
                <div class="text-base px-4 pb-4 md:pb-6 faq__a" v-html="faq.a"></div>
              </div>
            </div>
          </div>

        </FadeIn>

      </Container>
    </Section>

    <Section id="get-support" background="#e8e5de">
      <Container>

        <div class="mx-auto max-w-[880px]">

          <header class="flex flex-col md:text-center md:items-center mb-8 md:mb-10">
            <FadeIn>
              <div class="mb-2 md:mb-4">
                <span class="rounded-full bg-[#5a6ded] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
                  Help using Elker
                </span>
              </div>
              <h2 class="text-section-title">If you need support</h2>
              <div class="text-subtitle">
                Need help using Elker? If something about reporting is unclear, or you're not sure whether it's really anonymous, our support team can help. We can explain how the platform works. We can't see or act on a report you've made to your organisation, and we're not an emergency service.
              </div>
            </FadeIn>
          </header>

          <FadeIn class="mb-10 md:mb-12">

            <!-- Two ways to reach support, split into tabs so a reporter picks one
            path at a time. The tab is openable by URL hash: #get-support (or no
            hash) opens the message tab; #get-support-anonymous opens the anonymous
            tab. See selectSupportTabFromHash below. -->
            <div class="flex gap-2 mb-4 md:mb-6">
              <button
              type="button"
              :aria-pressed="supportTab === 'message'"
              @click="supportTab = 'message'"
              class="flex-1 rounded-xl border-2 px-4 py-3 text-sm md:text-base font-semibold transition"
              :class="supportTab === 'message' ? 'bg-[#5a6ded] border-[#5a6ded] text-white' : 'bg-white border-[#d7d4cd] text-gray-700 hover:border-[#5a6ded]'"
              >
                Send us a message
              </button>
              <button
              type="button"
              :aria-pressed="supportTab === 'anonymous'"
              @click="supportTab = 'anonymous'"
              class="flex-1 rounded-xl border-2 px-4 py-3 text-sm md:text-base font-semibold transition"
              :class="supportTab === 'anonymous' ? 'bg-[#5a6ded] border-[#5a6ded] text-white' : 'bg-white border-[#d7d4cd] text-gray-700 hover:border-[#5a6ded]'"
              >
                Stay anonymous
              </button>
            </div>

            <!-- Tab 1: reporter support form -> support@elker.com -->
            <div v-show="supportTab === 'message'" class="bg-white border-2 border-[#d7d4cd] rounded-[20px] p-6 md:p-8">
              <p class="text-[.95rem] leading-[1.6rem] text-gray-700 mb-5">
                Leave your email and we'll reply with help. We only use it to answer your question.
              </p>

              <div v-if="supportSent" class="rounded-xl bg-[#eef0ff] border-2 border-[#c0c7fa] p-5">
                <p class="text-[.95rem] leading-[1.6rem] text-gray-800">
                  Thanks. Your message has reached our support team and we'll get back to you.
                </p>
              </div>

              <form v-else @submit.prevent="submitSupport" novalidate>
                <div class="mb-4">
                  <label for="support-name" class="block text-sm font-bold text-gray-900 mb-1.5">Name (optional)</label>
                  <input id="support-name" v-model="supportName" @focus="formTracking.start()" type="text" autocomplete="off" placeholder="Your name" class="input-text" />
                </div>
                <div class="mb-4">
                  <label for="support-email" class="block text-sm font-bold text-gray-900 mb-1.5">Email</label>
                  <input id="support-email" v-model="supportEmail" @focus="formTracking.start()" type="email" autocomplete="off" placeholder="yourname@example.com" class="input-text" />
                </div>
                <div class="mb-4">
                  <label for="support-message" class="block text-sm font-bold text-gray-900 mb-1.5">How can we help?</label>
                  <textarea id="support-message" v-model="supportMessage" @focus="formTracking.start()" rows="4" placeholder="Tell us what you need help with" class="input-text"></textarea>
                </div>
                <!-- Honeypot: hidden from people, bots fill it -->
                <div aria-hidden="true" style="position: absolute; left: -9999px;">
                  <label for="support-website">Website</label>
                  <input id="support-website" v-model="supportWebsite" type="text" tabindex="-1" autocomplete="off" />
                </div>
                <button
                type="submit"
                :disabled="supportWaiting"
                class="inline-flex items-center justify-center rounded-md border border-transparent bg-[#5a6ded] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#3645d0] disabled:opacity-60"
                >
                  {{ supportWaiting ? 'Sending' : 'Send message' }}
                </button>
                <p v-if="supportError" class="mt-3 text-sm text-[#DE1A1A]">{{ supportError }}</p>
                <p class="mt-4 text-xs text-gray-500">
                  We handle your details in line with our <NuxtLink to="/policies/privacy" class="underline" target="_blank">Privacy Policy</NuxtLink>.
                </p>
              </form>
            </div>

            <!-- Tab 2: anonymous support channel -->
            <div v-show="supportTab === 'anonymous'" class="bg-white border-2 border-[#d7d4cd] rounded-[20px] p-6 md:p-8">
              <p class="text-[.95rem] leading-[1.6rem] text-gray-700 mb-5">
                Reach our support team without giving your name or email. Open our anonymous support channel and we'll help you there.
              </p>
              <a
              href="https://help.elker.com/report"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-[#5a6ded] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#3645d0]"
              >
                Contact support anonymously
              </a>
              <p class="mt-4 text-xs text-gray-500">
                This opens help.elker.com in a new tab. You do not need to enter your name or email.
              </p>
            </div>

          </FadeIn>

          <FadeIn>
            <div class="rounded-[20px] bg-white border-2 border-[#d7d4cd] p-6 md:p-8">
              <h3 class="text-base md:text-lg font-bold text-gray-900 mb-3">What we can help with</h3>
              <ul role="list" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <li v-for="item in supportTopics" :key="item" class="flex gap-3 items-start">
                  <div class="flex-none flex items-center justify-center h-6 w-6 rounded-full bg-[#5a6ded] mt-0.5">
                    <Icon name="material-symbols:check" class="h-4 w-4 text-white" />
                  </div>
                  <span class="text-[.95rem] leading-[1.5rem] text-gray-800">{{ item }}</span>
                </li>
              </ul>
            </div>
          </FadeIn>

          <FadeIn class="mt-6 md:mt-8">
            <div class="rounded-[20px] bg-white border-2 border-[#d7d4cd] p-6 md:p-8">
              <h3 class="text-base md:text-lg font-bold text-gray-900 mb-3">Further reading</h3>
              <p class="text-[.95rem] leading-[1.6rem] text-gray-700 mb-4">
                If you want to understand more before you decide, these explainers may help:
              </p>
              <ul role="list" class="space-y-2.5">
                <li v-for="link in furtherReading" :key="link.href" class="flex gap-3 items-start">
                  <div class="flex-none flex items-center justify-center h-6 w-6 rounded-full bg-[#eef0ff] border border-[#c0c7fa] mt-0.5">
                    <Icon name="iconoir:book-stack" class="h-3.5 w-3.5 text-[#3645d0]" />
                  </div>
                  <NuxtLink :to="link.href" class="text-[.95rem] leading-[1.5rem] text-[#3645d0] underline hover:text-[#2530a8]">
                    {{ link.label }}
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </FadeIn>

          <div class="mt-10 md:mt-12 text-center">
            <p class="text-sm text-gray-600">
              If this is an emergency or involves imminent harm, contact your local emergency services first. Elker is not an emergency reporting channel.
            </p>
          </div>

        </div>

      </Container>
    </Section>

  </main>
</template>

<script setup>
const route = useRoute()

const activePanel = ref(999)
const togglePanel = index => {
  activePanel.value = activePanel.value === index ? 999 : index
}

// Support section tabs. Default to the message form; the anonymous tab can be
// opened directly by URL hash so links elsewhere on the page (and the reporter
// header) can deep-link the right path:
//   #get-support            -> message tab (also the bare anchor)
//   #get-support-anonymous  -> anonymous tab
const supportTab = ref('message')

// Open a support tab and scroll the section into view. Used by the in-page
// links (via @click, so they work even when the hash already matches and no
// hashchange would fire) and by selectSupportTabFromHash for cross-page loads.
const openSupport = (tab) => {
  supportTab.value = tab === 'anonymous' ? 'anonymous' : 'message'
  if (!process.client) return
  // Wait for the panel swap to lay out before scrolling, so we land on the
  // section rather than a pre-shift offset.
  nextTick(() => {
    document.getElementById('get-support')?.scrollIntoView({ behavior: 'smooth' })
  })
}

// Cross-page entry: a link from elsewhere (e.g. the header CTA) lands with a
// hash. Read it on mount and on hashchange to open the matching tab.
const selectSupportTabFromHash = () => {
  if (!process.client) return
  const hash = window.location.hash
  if (hash === '#get-support-anonymous') {
    supportTab.value = 'anonymous'
    nextTick(() => document.getElementById('get-support')?.scrollIntoView({ behavior: 'smooth' }))
  } else if (hash === '#get-support') {
    supportTab.value = 'message'
  }
}

onMounted(() => {
  selectSupportTabFromHash()
  window.addEventListener('hashchange', selectSupportTabFromHash)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', selectSupportTabFromHash)
})

// Reporter support form. Posts to /api/reporterSupport, which only emails
// support@elker.com (no CRM lead, no ad conversion, no marketing). A reporter is
// not a lead, so this is deliberately separate from the /contact flow.
const supportName = ref('')
const supportEmail = ref('')
const supportMessage = ref('')
const supportWebsite = ref('') // honeypot
const supportFormLoadedAt = ref(Date.now())
const supportWaiting = ref(false)
const supportError = ref(null)
const supportSent = ref(false)

const supportTopics = [
  'How anonymity works and what Elker can and can’t see',
  'Logging back in to your dashboard',
  'Adding to or updating a report you’ve already made',
  'Understanding what happens after you submit'
]

// Hand-picked reporter-relevant explainers (not the blog index, which is a buyer
// browse surface). Chosen for genuine reporter relevance and proven search
// interest: what protection you have if you're punished for reporting, how
// anonymity works, and what whistleblowing means.
const furtherReading = [
  { label: 'What unlawful victimisation is, and your protection if you speak up', href: '/articles/what-is-unlawful-victimisation-in-the-workplace' },
  { label: 'How anonymous reporting works in practice', href: '/articles/anonymous-reporting-advantages-disadvantages' },
  { label: 'What whistleblowing is and who is protected', href: '/articles/what-is-whistleblowing' }
]

// Funnel tracking. This is the reporter-support form, deliberately separate
// from the sales forms: it must never be read as a lead.
const formTracking = useFormTracking('reporter-support')

const submitSupport = async () => {
  supportError.value = null
  formTracking.submitted()

  if (!supportEmail.value || !supportMessage.value) {
    supportError.value = 'Please add a message and an email so we can reply.'
    formTracking.failed('missing-required-fields')
    return
  }

  supportWaiting.value = true

  try {
    const res = await $fetch('/api/reporterSupport', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: supportName.value,
        email: supportEmail.value,
        message: supportMessage.value,
        website: supportWebsite.value,
        formLoadedAt: supportFormLoadedAt.value
      })
    })

    if (res.ok) {
      supportSent.value = true
      formTracking.ok({ recorded: res.recorded !== false })
    } else {
      supportError.value = res.message || 'There has been an error. Please email us at support@elker.com'
      formTracking.failed('server-rejected')
    }
  } catch (e) {
    supportError.value = 'There has been an error. Please email us at support@elker.com'
    formTracking.failed('request-failed')
  } finally {
    supportWaiting.value = false
  }
}

const journeySteps = [
  {
    icon: 'iconoir:internet',
    title: 'Open your organisation\u2019s page',
    body: 'You arrive at a secure page hosted by Elker on your organisation\u2019s behalf. No account, no sign-in required to start.'
  },
  {
    icon: 'iconoir:book-stack',
    title: 'Read what\u2019s available',
    body: 'Your organisation’s Elker start page explains what you can report through the Elker channel, who sees and reviews reports, and what happens next.'
  },
  {
    icon: 'iconoir:edit-pencil',
    title: 'Answer a few questions',
    body: 'A short form asks for the details your organisation needs to look into what happened. You decide how much to share and whether to include your name.'
  },
  {
    icon: 'iconoir:send',
    title: 'Submit your report',
    body: 'Your report is encrypted and delivered to a small, named group of reviewers. You receive a unique case reference at this point.'
  },
  {
    icon: 'iconoir:message-text',
    title: 'Return to your dashboard',
    body: 'Sign back in with whatever option you set up: email, phone number, username and password, the Elker app, or a one-time login key. Read updates, answer questions from the reviewer, or add more information, without revealing your identity.'
  }
]

const protections = [
  {
    icon: 'material-symbols:visibility-off-outline',
    title: 'Anonymity by design',
    body: 'You can report without giving your name, email, or phone. Your case is identified by a unique reference, and the tracking your device normally sends is removed before anything reaches your reviewer.'
  },
  {
    icon: 'material-symbols:lan-outline',
    title: 'Separate from employer systems',
    body: 'Elker runs on independent infrastructure. Your report never passes through your employer’s email, file shares, or staff IT systems.'
  },
  {
    icon: 'material-symbols:lock-outline',
    title: 'Encrypted at every step',
    body: 'Every report, message, and file is locked with strong, industry-standard encryption while it travels to Elker and while it’s stored. Anyone watching the connection sees scrambled data, not what you wrote.'
  },
  {
    icon: 'material-symbols:key-outline',
    title: 'Role-based access',
    body: 'Only the reviewers your organisation has nominated can open a case. Managers, IT, and general staff have no access. Every view, edit, and download is logged so nothing happens off the record.'
  },
  {
    icon: 'material-symbols:database-outline',
    title: 'Your organisation\u2019s own space',
    body: 'Your organisation\u2019s data is held in a dedicated space, separate from every other Elker client. No other organisation can see your reports, and your reports are never mixed with anyone else\u2019s.'
  },
  {
    icon: 'material-symbols:public',
    title: 'Stored on trusted cloud infrastructure',
    body: 'Your data is held on Amazon\u2019s secure cloud and stays encrypted while it\u2019s stored. Both Amazon and Elker are reviewed by independent security auditors every year.'
  },
  {
    icon: 'iconoir:eye-closed',
    title: 'Strictly limited Elker staff access',
    body: 'Elker employees can\u2019t read your reports in normal operations. Only the reviewers your organisation has nominated can open a submitted report, and every access is logged.'
  },
  {
    icon: 'material-symbols:verified-outline',
    title: 'Independently audited',
    body: 'Elker holds ISO 27001 and SOC 2, independent security certifications that mean our controls, processes, and infrastructure are checked every year by outside auditors.'
  },
  {
    icon: 'material-symbols:history-toggle-off',
    title: 'Retention and deletion',
    body: 'Your organisation sets how long closed cases are kept. When data is deleted, it is removed from our systems and any backup copies expire on a fixed timetable.'
  }
]

const commitments = [
  'We don\u2019t share your report with your employer beyond the information you submit and the messages you choose to send.',
  'We don\u2019t use your report content to train or fine-tune AI models. Your words are never added to any training dataset, by Elker or anyone else.',
  'We don\u2019t sell, rent, or share report data with advertisers, data brokers, or any third party for marketing or research.',
  'We don\u2019t keep a record of your computer\u2019s internet address (your IP) beyond the moment your browser first connects to us. It never appears next to anything you write.',
  'We don\u2019t keep deleted cases on our systems once your organisation\u2019s retention period ends, and any backup copies expire on the same timetable.',
  'We don\u2019t require your name, email, or phone number to submit a report. Sharing those details is your choice.'
]

const faqs = [
  {
    q: 'Can my employer find out it was me?',
    a: 'If you submit anonymously, your employer sees the content of your report and any messages you send, but not your identity. The reviewer cannot unmask you. For more on how anonymity works in practice, see our explainer on <a href="/articles/anonymous-reporting-advantages-disadvantages" class="underline">anonymous reporting</a>.'
  },
  {
    q: 'Can Elker staff read my report?',
    a: 'Not in the course of normal operations. Elker employees access an individual case only at the written request of your organisation\u2019s admin, typically for a specific support issue, and every such access is recorded in the audit trail.'
  },
  {
    q: 'What happens if I want to add something later?',
    a: 'Sign back in with your chosen login option. You can add messages, attach more evidence, and see updates from the reviewer without revealing your identity.'
  },
  {
    q: 'Can I submit more than one report?',
    a: 'Yes. Every report you submit is isolated from the others inside the platform. Submitting multiple reports from the same dashboard does not link them together in a way that would risk identifying you.'
  },
  {
    q: 'What if I can\u2019t log back into my dashboard?',
    a: 'Because anonymous reports are genuinely anonymous, Elker has no way to verify who you are if you lose access to your login. We can\u2019t reset a password or send you a new login link the way a typical web service can. If you do lose access, the best step is to submit a new report that briefly references your earlier one; the reviewer can then link the two and continue the conversation with you.'
  },
  {
    q: 'When might the law require my information to be disclosed?',
    a: 'Information you give Elker is treated as confidential by your organisation. The law requires disclosure only in narrow situations: when there is a serious risk to someone\u2019s safety, when a child\u2019s safety is involved, when mandatory reporting rules apply, or when a regulator or law enforcement body makes a lawful request. Your organisation\u2019s own policy will spell out exactly what applies in your context.'
  },
  {
    q: 'What if I change my mind and want to withdraw?',
    a: 'You can ask the reviewer to close the case via the dashboard. Your organisation will handle the request in line with their own policy; they may still need to act on safety-critical information.'
  },
  {
    q: 'Is there a time limit on when I can report?',
    a: 'Elker itself does not impose a time limit. Your organisation\u2019s own policy, and any relevant legislation, may set timeframes for specific concern types. The guidance on your organisation\u2019s reporting page will make this clear.'
  },
  {
    q: 'What if I\u2019m not sure this belongs here?',
    a: 'Submit it anyway. Reviewers are trained to triage and redirect concerns that belong elsewhere, and you will receive an explanation via the dashboard if that happens. Nothing about a redirected report is shared back with the broader organisation.'
  }
]

const seo = useSeo({
  meta_title: 'How reporting works | Elker',
  meta_description: 'How Elker keeps a report private: you can report anonymously, everything is encrypted, and only the reviewers your organisation has nominated can read it.',
  meta_image: null
}, route.fullPath)

useHead(seo)
</script>
